import { __assign, __rest } from "tslib";
import * as React from 'react';
import * as PropTypes from 'prop-types';
import { nanoid } from 'nanoid';
import { withFocus } from '../utils/withFocus';
import { CloseSmall } from '../icons/icons/CloseSmall';
import { AlertSuccessSmall } from '../icons/icons/AlertSuccessSmall';
import { AlertInfoSmall } from '../icons/icons/AlertInfoSmall';
import { AlertWarningSmall } from '../icons/icons/AlertWarningSmall';
import { AlertConnectionLostSmall } from '../icons/icons/AlertConnectionLostSmall';
import { AlertErrorSmall } from '../icons/icons/AlertErrorSmall';
import { Link } from '../Link';
import { classNames } from '../utils/stringUtils';
import styles from './Alert.module.css';
import tokens from '@metro-ui/tokens/css/custom-properties.module.css';
import { IconButton } from '../Button';
var AlertVariantIconMap = {
    success: AlertSuccessSmall,
    info: AlertInfoSmall,
    warning: AlertWarningSmall,
    offline: AlertConnectionLostSmall,
    error: AlertErrorSmall,
};
var iconSizeMap = {
    small: 16 / 24,
    medium: 1,
    large: 1,
};
var AlertComponent = React.forwardRef(function (_a, ref) {
    var _b, _c;
    var variant = _a.variant, _d = _a.size, size = _d === void 0 ? 'medium' : _d, title = _a.title, _e = _a.titleId, titleId = _e === void 0 ? nanoid() : _e, _f = _a.shouldHideTitle, shouldHideTitle = _f === void 0 ? false : _f, hasRequiredInteraction = _a.hasRequiredInteraction, actions = _a.actions, _g = _a.isOverarching, isOverarching = _g === void 0 ? false : _g, _h = _a.isFloating, isFloating = _h === void 0 ? false : _h, onCtaClick = _a.onCtaClick, ctaText = _a.ctaText, ctaHref = _a.ctaHref, _j = _a.closeIconLabel, closeIconLabel = _j === void 0 ? 'Close Alert' : _j, _k = _a.hasPointer, hasPointer = _k === void 0 ? false : _k, _l = _a.maxWidth, maxWidth = _l === void 0 ? 1920 : _l, _m = _a.className, className = _m === void 0 ? '' : _m, _o = _a.contentId, contentId = _o === void 0 ? nanoid() : _o, children = _a.children, htmlAttributes = __rest(_a, ["variant", "size", "title", "titleId", "shouldHideTitle", "hasRequiredInteraction", "actions", "isOverarching", "isFloating", "onCtaClick", "ctaText", "ctaHref", "closeIconLabel", "hasPointer", "maxWidth", "className", "contentId", "children"]);
    var actualSize = isOverarching ? 'medium' : size;
    var Icon = AlertVariantIconMap[variant];
    var showCta = !!onCtaClick || !!ctaText;
    var ctaRef = !hasRequiredInteraction || (actions && actions.length > 0)
        ? undefined
        : ref;
    var alertWrapperClassNames = classNames((_b = {},
        _b[tokens.mdsComp] = true,
        _b[styles.mdsCompAlertWrapper] = true,
        _b[styles.withMaxWidth] = !!maxWidth,
        _b[styles.floating] = isFloating,
        _b[className] = true,
        _b));
    var alertClassNames = classNames((_c = {},
        _c[styles.mdsCompAlert] = true,
        _c[styles.overarching] = isOverarching,
        _c[styles.hasPointer] = hasPointer,
        _c[styles.shouldHideTitle] = shouldHideTitle,
        _c[styles[variant]] = true,
        _c[styles[actualSize]] = true,
        _c));
    var alertMaxWidthStyle = !isFloating && maxWidth ? { maxWidth: "".concat(maxWidth, "px") } : undefined;
    return (React.createElement("div", { className: alertWrapperClassNames, style: alertMaxWidthStyle },
        React.createElement("div", __assign({}, htmlAttributes, { role: hasRequiredInteraction ? 'alertdialog' : 'alert', "aria-labelledby": titleId, "aria-describedby": children ? contentId : undefined, className: alertClassNames }),
            React.createElement("div", { className: styles.mdsCompAlertIconWrapper },
                React.createElement(Icon, { className: styles.mdsCompAlertIcon, size: iconSizeMap[actualSize] })),
            React.createElement("div", { className: styles.mdsCompAlertContent },
                title && (React.createElement("div", { className: styles.mdsCompAlertTitle, id: titleId }, title)),
                children && React.createElement("div", { id: contentId }, children),
                actions && (React.createElement("div", { className: styles.mdsCompAlertActions }, actions.map(function (_a, index) {
                    var text = _a.text, key = _a.key, href = _a.href, linkProps = __rest(_a, ["text", "key", "href"]);
                    return (React.createElement(Link, __assign({ className: styles.mdsCompAlertAction, ref: hasRequiredInteraction && index === 0 ? ref : undefined, href: href, key: href || key }, linkProps), text));
                })))),
            showCta &&
                (ctaText ? (React.createElement(Link, { className: styles.mdsCompAlertCta, ref: ctaRef, onClick: onCtaClick, href: ctaHref }, ctaText)) : (React.createElement(IconButton, { className: styles.mdsCompAlertClose, ref: ctaRef, Icon: CloseSmall, label: closeIconLabel, size: "small", onClick: onCtaClick, hasNoInteractionEffect: true }))))));
});
AlertComponent.propTypes = {
    variant: PropTypes.oneOf(['info', 'warning', 'error', 'success', 'offline'])
        .isRequired,
    size: PropTypes.oneOf(['small', 'medium', 'large']),
    title: PropTypes.node,
    titleId: PropTypes.string,
    contentId: PropTypes.string,
    shouldHideTitle: PropTypes.bool,
    actions: PropTypes.arrayOf(PropTypes.shape({
        text: PropTypes.node.isRequired,
        key: PropTypes.string,
    })),
    isOverarching: PropTypes.bool,
    isFloating: PropTypes.bool,
    onCtaClick: PropTypes.func,
    ctaText: PropTypes.node,
    ctaHref: PropTypes.string,
    closeIconLabel: PropTypes.string,
    hasPointer: PropTypes.bool,
    hasRequiredInteraction: PropTypes.bool,
    className: PropTypes.string,
    children: PropTypes.node,
    maxWidth: PropTypes.oneOf([1920, 1440, null]),
};
export var Alert = withFocus(AlertComponent);
//# sourceMappingURL=Alert.js.map